import React, { useState, useEffect } from 'react';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import '../../styles/paynow-config.css';

const PayNowConfiguration = () => {
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSalon, setSelectedSalon] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    salonId: '',
    salonName: '',
    uen: '',
    sgqrId: '',
    bankAccountNumber: '',
    qrCodeImageUrl: '',
    apiClientId: '',
    apiClientSecret: '',
    apiKey: '',
    accessToken: '',
    apiBaseUrl: 'https://api.ocbc.com/transactional/paynowqr/1.0',
    webhookSecret: '',
    isActive: true,
    hasExistingApiCredentials: false,
    hasExistingAccessToken: false
  });

  useEffect(() => {
    loadSalons();
  }, []);

  const loadSalons = async () => {
    try {
      setLoading(true);
      const salonsSnapshot = await getDocs(collection(db, 'salons'));
      const salonsList = [];

      salonsSnapshot.forEach((docSnap) => {
        const salonData = docSnap.data();
        const config = salonData.paynowConfig || {};
        salonsList.push({
          salonId: docSnap.id,
          salonName: salonData.name || docSnap.id,
          uen: config.uen || '',
          sgqrId: config.sgqrId || '',
          bankAccountNumber: config.bankAccountNumber || '',
          qrCodeImageUrl: config.qrCodeImageUrl || '',
          isActive: config.isActive !== undefined ? config.isActive : false,
          hasApiCredentials: !!(config.apiClientId && config.apiClientSecret),
          hasAccessToken: !!config.accessToken
        });
      });

      setSalons(salonsList);
    } catch (error) {
      console.error('Error loading PayNow configurations:', error);
      setMessage({ type: 'error', text: 'Failed to load PayNow configurations' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditConfig = async (salonId) => {
    try {
      const salonDoc = await getDoc(doc(db, 'salons', salonId));

      if (salonDoc.exists()) {
        const salonData = salonDoc.data();
        const config = salonData.paynowConfig || {};
        const hasApiCreds = !!(config.apiClientId && config.apiClientSecret);
        const hasToken = !!config.accessToken;

        setFormData({
          salonId: salonId,
          salonName: salonData.name || salonId,
          uen: config.uen || '',
          sgqrId: config.sgqrId || '',
          bankAccountNumber: config.bankAccountNumber || '',
          qrCodeImageUrl: config.qrCodeImageUrl || '',
          apiClientId: '',
          apiClientSecret: '',
          apiKey: '',
          accessToken: '',
          apiBaseUrl: config.apiBaseUrl || 'https://api.ocbc.com/transactional/paynowqr/1.0',
          webhookSecret: '',
          isActive: config.isActive !== undefined ? config.isActive : true,
          hasExistingApiCredentials: hasApiCreds,
          hasExistingAccessToken: hasToken
        });
        setSelectedSalon(salonId);
        setShowConfigForm(true);
      } else {
        setMessage({ type: 'error', text: 'Salon not found' });
      }
    } catch (error) {
      console.error('Error loading salon config:', error);
      setMessage({ type: 'error', text: 'Failed to load salon configuration' });
    }
  };

  const handleCreateNew = () => {
    setFormData({
      salonId: '',
      salonName: '',
      uen: '',
      sgqrId: '',
      bankAccountNumber: '',
      qrCodeImageUrl: '',
      apiClientId: '',
      apiClientSecret: '',
      apiKey: '',
      accessToken: '',
      apiBaseUrl: 'https://api.ocbc.com/transactional/paynowqr/1.0',
      webhookSecret: '',
      isActive: true,
      hasExistingApiCredentials: false,
      hasExistingAccessToken: false
    });
    setSelectedSalon(null);
    setShowConfigForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file' });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size should be less than 2MB' });
      return;
    }

    try {
      // Convert to base64 for storage
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          qrCodeImageUrl: reader.result
        }));
        setMessage({ type: 'success', text: 'QR code image uploaded successfully' });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: 'Failed to upload image' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.salonId || !formData.uen || !formData.sgqrId || !formData.bankAccountNumber) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Build the paynowConfig object
      const paynowConfig = {
        salonName: formData.salonName,
        uen: formData.uen,
        sgqrId: formData.sgqrId,
        bankAccountNumber: formData.bankAccountNumber,
        qrCodeImageUrl: formData.qrCodeImageUrl,
        apiBaseUrl: formData.apiBaseUrl,
        isActive: formData.isActive,
        updatedAt: serverTimestamp()
      };

      // Only include API credentials if new values were provided
      if (formData.apiClientId) {
        paynowConfig.apiClientId = formData.apiClientId;
      }
      if (formData.apiClientSecret) {
        paynowConfig.apiClientSecret = formData.apiClientSecret;
      }
      if (formData.apiKey) {
        paynowConfig.apiKey = formData.apiKey;
      }
      if (formData.accessToken) {
        paynowConfig.accessToken = formData.accessToken;
      }
      if (formData.webhookSecret) {
        paynowConfig.webhookSecret = formData.webhookSecret;
      }

      // If editing and keeping existing credentials, preserve them
      if (selectedSalon && (formData.hasExistingApiCredentials || formData.hasExistingAccessToken)) {
        const existingDoc = await getDoc(doc(db, 'salons', formData.salonId));
        if (existingDoc.exists()) {
          const existingConfig = existingDoc.data().paynowConfig || {};
          if (!formData.apiClientId && existingConfig.apiClientId) {
            paynowConfig.apiClientId = existingConfig.apiClientId;
          }
          if (!formData.apiClientSecret && existingConfig.apiClientSecret) {
            paynowConfig.apiClientSecret = existingConfig.apiClientSecret;
          }
          if (!formData.apiKey && existingConfig.apiKey) {
            paynowConfig.apiKey = existingConfig.apiKey;
          }
          if (!formData.accessToken && existingConfig.accessToken) {
            paynowConfig.accessToken = existingConfig.accessToken;
          }
          if (!formData.webhookSecret && existingConfig.webhookSecret) {
            paynowConfig.webhookSecret = existingConfig.webhookSecret;
          }
        }
      }

      // Update the salon document with the paynowConfig
      const salonRef = doc(db, 'salons', formData.salonId);
      await updateDoc(salonRef, {
        paynowConfig: paynowConfig,
        updatedAt: serverTimestamp()
      });

      setMessage({ type: 'success', text: 'PayNow configuration saved successfully' });
      setShowConfigForm(false);
      loadSalons();
    } catch (error) {
      console.error('Error saving PayNow config:', error);
      setMessage({ type: 'error', text: 'Failed to save PayNow configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowConfigForm(false);
    setSelectedSalon(null);
    setMessage({ type: '', text: '' });
  };

  if (loading) {
    return <div className="loading-spinner">Loading PayNow configurations...</div>;
  }

  return (
    <div className="paynow-configuration">
      <div className="paynow-header">
        <div>
          <h2>PayNow Configuration</h2>
          <p className="paynow-subtitle">Configure SGQR and PayNow settings for each salon</p>
        </div>
        <button className="btn-primary" onClick={handleCreateNew}>
          + Add New Configuration
        </button>
      </div>

      {message.text && (
        <div className={`message-banner ${message.type}`}>
          {message.text}
        </div>
      )}

      {!showConfigForm ? (
        <div className="paynow-list">
          {salons.length === 0 ? (
            <div className="empty-state">
              <p>No PayNow configurations found</p>
              <p className="empty-state-hint">Click "Add New Configuration" to get started</p>
            </div>
          ) : (
            <div className="config-cards">
              {salons.map(salon => (
                <div key={salon.salonId} className="config-card">
                  <div className="config-card-header">
                    <h3>{salon.salonName || salon.salonId}</h3>
                    <span className={`status-badge ${salon.isActive ? 'active' : 'inactive'}`}>
                      {salon.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="config-card-body">
                    <div className="config-field">
                      <label>Salon ID:</label>
                      <span>{salon.salonId}</span>
                    </div>
                    <div className="config-field">
                      <label>UEN:</label>
                      <span>{salon.uen}</span>
                    </div>
                    <div className="config-field">
                      <label>SGQR ID:</label>
                      <span>{salon.sgqrId}</span>
                    </div>
                    <div className="config-field">
                      <label>Bank Account:</label>
                      <span>{salon.bankAccountNumber}</span>
                    </div>
                    <div className="config-field">
                      <label>QR Code:</label>
                      <span>{salon.qrCodeImageUrl ? 'Uploaded' : 'Not uploaded'}</span>
                    </div>
                    <div className="config-field">
                      <label>API Configured:</label>
                      <span>{salon.hasApiCredentials ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="config-field">
                      <label>Access Token:</label>
                      <span>{salon.hasAccessToken ? 'Configured' : 'Not set'}</span>
                    </div>
                  </div>
                  <div className="config-card-footer">
                    <button
                      className="btn-secondary"
                      onClick={() => handleEditConfig(salon.salonId)}
                    >
                      Edit Configuration
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="paynow-form-container">
          <form onSubmit={handleSubmit} className="paynow-form">
            <div className="form-section">
              <h3>Basic Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Salon ID *</label>
                  <input
                    type="text"
                    name="salonId"
                    value={formData.salonId}
                    onChange={handleInputChange}
                    placeholder="e.g., demo-salon"
                    required
                    disabled={!!selectedSalon}
                  />
                  <small>Unique identifier for the salon</small>
                </div>
                <div className="form-group">
                  <label>Salon Name</label>
                  <input
                    type="text"
                    name="salonName"
                    value={formData.salonName}
                    onChange={handleInputChange}
                    placeholder="e.g., Demo Salon"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>PayNow Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>UEN (Unique Entity Number) *</label>
                  <input
                    type="text"
                    name="uen"
                    value={formData.uen}
                    onChange={handleInputChange}
                    placeholder="e.g., 201234567A"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>SGQR ID *</label>
                  <input
                    type="text"
                    name="sgqrId"
                    value={formData.sgqrId}
                    onChange={handleInputChange}
                    placeholder="Your SGQR ID"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Bank Account Number *</label>
                  <input
                    type="text"
                    name="bankAccountNumber"
                    value={formData.bankAccountNumber}
                    onChange={handleInputChange}
                    placeholder="Your OCBC account number"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>QR Code Image</h3>
              <div className="form-group">
                <label>Upload SGQR Code Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="file-input"
                />
                <small>Upload your SGQR code image (PNG, JPG, max 2MB)</small>
                {formData.qrCodeImageUrl && (
                  <div className="qr-preview">
                    <img src={formData.qrCodeImageUrl} alt="QR Code Preview" />
                  </div>
                )}
              </div>
            </div>

            <div className="form-section">
              <h3>OCBC API Configuration</h3>
              <p className="section-description">
                Configure your OCBC Corporate PayNow API credentials to enable dynamic QR code generation with pre-filled amounts.
                {(formData.hasExistingApiCredentials || formData.hasExistingAccessToken) && (
                  <strong> Some credentials are already configured. Leave fields blank to keep existing values, or enter new values to update them.</strong>
                )}
                {!formData.hasExistingApiCredentials && !formData.hasExistingAccessToken && ' Fill in the Access Token to enable dynamic QR codes.'}
              </p>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Access Token (Bearer) {formData.hasExistingAccessToken && <span className="existing-indicator">Configured</span>}</label>
                  <input
                    type="password"
                    name="accessToken"
                    value={formData.accessToken}
                    onChange={handleInputChange}
                    placeholder={formData.hasExistingAccessToken ? "Leave blank to keep existing token" : "Paste your OCBC API Bearer token here"}
                  />
                  <small>The Bearer token from your OCBC Sandbox/Production application. Required for dynamic QR code generation.</small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>API Client ID {formData.hasExistingApiCredentials && <span className="existing-indicator">Configured</span>}</label>
                  <input
                    type="text"
                    name="apiClientId"
                    value={formData.apiClientId}
                    onChange={handleInputChange}
                    placeholder={formData.hasExistingApiCredentials ? "Leave blank to keep existing value" : "Your OCBC API Client ID"}
                  />
                </div>
                <div className="form-group">
                  <label>API Client Secret {formData.hasExistingApiCredentials && <span className="existing-indicator">Configured</span>}</label>
                  <input
                    type="password"
                    name="apiClientSecret"
                    value={formData.apiClientSecret}
                    onChange={handleInputChange}
                    placeholder={formData.hasExistingApiCredentials ? "Leave blank to keep existing value" : "Your OCBC API Client Secret"}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>API Key</label>
                  <input
                    type="password"
                    name="apiKey"
                    value={formData.apiKey}
                    onChange={handleInputChange}
                    placeholder={formData.hasExistingApiCredentials ? "Leave blank to keep existing value" : "Your OCBC API Key (if applicable)"}
                  />
                </div>
                <div className="form-group">
                  <label>API Base URL</label>
                  <input
                    type="text"
                    name="apiBaseUrl"
                    value={formData.apiBaseUrl}
                    onChange={handleInputChange}
                    placeholder="https://api.ocbc.com/transactional/paynowqr/1.0"
                  />
                  <small>Default: https://api.ocbc.com/transactional/paynowqr/1.0</small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Webhook Secret</label>
                  <input
                    type="password"
                    name="webhookSecret"
                    value={formData.webhookSecret}
                    onChange={handleInputChange}
                    placeholder={formData.hasExistingApiCredentials ? "Leave blank to keep existing value" : "Secret key for webhook verification"}
                  />
                  <small>Used to verify webhook requests from OCBC (if applicable)</small>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <span>Enable PayNow for this salon</span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : selectedSalon ? 'Update Configuration' : 'Create Configuration'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PayNowConfiguration;
